/**
 * "Was ist zu tun?" of the inquiry panel: box per situation with exactly its actions, texts / colors / icons of the actions
 * (part of helpers/substitutions/overview.ts, re-exported there). Pure, checked in tests/substitutions/overview.check.ts.
 */
import {SubstitutionOverviewSituation} from '@/enums/substitution-manager/SubstitutionOverview';
import {countdownIn, dateTimeText} from '@/helpers/substitutions/overviewFormat';
import type {OverviewTone} from '@/helpers/substitutions/overview';

const S = SubstitutionOverviewSituation;

/* ================================================================ "Was ist zu tun?" */

export type DetailAction = 'send' | 'search' | 'ask' | 'cancel' | 'stop' | 'choose' | 'reask' | 'sendCancellation';

export interface SituationBox {
  title: string;
  text: string;
  tone: OverviewTone;
  countdown?: string;
  actions: DetailAction[];
}

export interface SituationBoxContext {
  now: Date;
  autoCancelAt?: Date;
  autoCancelStopped: boolean;
  /** Somebody is on the waiting list after the office revoked the selected confirmation */
  waitingListFree: boolean;
  studentCount?: number;
  /** "eine WhatsApp" / "eine E-Mail" ('' unknown) */
  parentsChannel?: string;
  substituteName?: string;
}

/**
 * Box "Was ist zu tun?" of the panel with exactly the actions of the situation (no second cancel button elsewhere):
 * search / ask more teachers (info), cancel now (error), stop the automatic cancellation (warning) …
 */
export function situationBox(situation: SubstitutionOverviewSituation, ctx: SituationBoxContext): SituationBox | undefined {
  const kids = ctx.studentCount ? ` der ${ctx.studentCount === 1 ? '1 Kind' : `${ctx.studentCount} Kinder`}`.replace('der 1 Kind', 'des Kindes') : '';
  const parents = ctx.parentsChannel ? ` – die Eltern${kids} bekommen ${ctx.parentsChannel}` : '';
  const auto = ctx.autoCancelAt
    ? `Wenn niemand zusagt, sagt Vanilla die Stunde am ${dateTimeText(ctx.autoCancelAt)} automatisch ab${parents}.`
    : ctx.autoCancelStopped
      ? 'Die automatische Absage wurde gestoppt – ohne Entscheidung findet die Stunde ohne Lehrkraft statt.'
      : 'Es ist keine automatische Absage geplant – ohne Entscheidung findet die Stunde ohne Lehrkraft statt.';
  const stop: DetailAction[] = ctx.autoCancelAt ? ['stop'] : [];
  const countdown = ctx.autoCancelAt ? `Absage ${countdownIn(ctx.autoCancelAt, ctx.now)}` : undefined;
  const reask: DetailAction[] = ctx.waitingListFree ? ['reask'] : [];
  switch (situation) {
    case S.NOT_SENT:
      return {title: 'Anfrage noch nicht versendet', text: `Die Lehrkräfte wissen noch nichts von der Anfrage. ${ctx.autoCancelAt ? auto : ''}`.trim(),
        tone: 'warn', countdown, actions: ['send', 'cancel', ...stop]};
    case S.REPORTED_ONLY:
      return {title: 'Ausfall gemeldet – noch keine Suche',
        text: 'Die Lehrkraft hat den Ausfall gemeldet. Es wurde noch niemand angefragt und es ist keine automatische Absage geplant – ohne Entscheidung findet die Stunde ohne Lehrkraft statt.',
        tone: 'warn', actions: ['search', 'cancel']};
    case S.NO_CANDIDATES:
      return {title: 'Keine Vertretung gefunden', text: `Niemand konnte angefragt werden. ${auto}`, tone: 'bad', countdown, actions: ['search', 'cancel', ...stop]};
    case S.ALL_DECLINED:
      return {title: 'Alle Angefragten haben abgesagt', text: auto, tone: 'bad', countdown, actions: [...reask, 'ask', 'cancel', ...stop]};
    case S.WITHDRAWN:
      return {title: 'Vertretung zurückgezogen – niemand mehr offen', text: auto, tone: 'warn', countdown, actions: [...reask, 'ask', 'cancel', ...stop]};
    case S.SEARCHING:
      return {title: 'Suche läuft', text: ctx.waitingListFree ? `Die Vertretung ist wieder frei. ${auto}` : auto, tone: 'info', countdown,
        actions: [...reask, 'ask', 'cancel', ...stop]};
    case S.CHOOSE:
      // revoked by the office and people on the waiting list: choose directly or ask the waiting list again (first one wins)
      return ctx.waitingListFree
        ? {title: 'Vertretung wieder frei – Warteliste', tone: 'warn', actions: ['choose', 'reask'],
          text: 'Auf der Warteliste stehen Lehrkräfte. Entweder die erste direkt als Vertretung auswählen oder die Warteliste erneut anfragen – wer zuerst bestätigt, bekommt die Stunde.'}
        : {title: 'Zusage liegt vor', text: 'Bitte unter „Angefragt“ als Vertretung auswählen (Menü der Person). Bis dahin ist die Stunde nicht besetzt.',
          tone: 'warn', actions: ['choose']};
    case S.CANCEL_NOT_SENT:
      return {title: 'Absage noch nicht versendet', text: 'Die Absage ist gespeichert. Standort und Eltern erfahren erst mit dem Senden vom Ausfall.',
        tone: 'warn', actions: ['sendCancellation']};
    case S.FOUND:
      return {title: 'Vertretung steht', text: ctx.substituteName ? `${ctx.substituteName} vertritt.` : 'Die Stunde ist vertreten.', tone: 'good', actions: []};
    case S.COMPENSATION_OPEN:
      return {title: 'Abgesagt – Kompensation offen', text: 'Lege im Reiter „Nachholen“ fest, wie die Stunde ausgeglichen wird.', tone: 'comp', actions: []};
    case S.CANCELLING:
      return {title: 'Absage wird versendet', text: 'Eltern und Standort werden gerade informiert.', tone: 'muted', actions: []};
    case S.CANCELLED:
      return {title: 'Abgesagt', text: 'Die Stunde ist abgesagt.', tone: 'muted', actions: []};
    case S.PAST:
      return {title: 'Vorbei – ohne Vertretung', text: 'Der Tag ist vorbei. Es wurde weder eine Vertretung gefunden noch abgesagt.', tone: 'muted', actions: []};
    case S.FOLLOW_UP_OPEN:
      return {title: 'Vorbei – Nachbereitung offen', tone: 'warn', actions: [],
        text: 'Der Tag ist vorbei. Es gab keine bestätigte Vertretung und keine versendete Absage. Bitte unten unter „Nachbereitung“ festhalten, was passiert ist, und bei einem Ausfall die Kompensation festlegen. Es wird nichts automatisch gesendet.'};
    case S.FOLLOW_UP_DONE:
      return {title: 'Nachbereitet', text: 'Die Nachbereitung ist abgeschlossen. Details unten unter „Nachbereitung“.', tone: 'muted', actions: []};
    default:
      return undefined;
  }
}

export const DETAIL_ACTION_TEXTS: Record<DetailAction, string> = {
  send: 'Prüfen und senden',
  search: 'Vertretung suchen',
  ask: 'Weitere Lehrkräfte anfragen',
  cancel: 'Jetzt absagen',
  stop: 'Automatische Absage stoppen',
  choose: 'Erste Zusage als Vertretung auswählen',
  reask: 'Warteliste erneut anfragen',
  sendCancellation: 'Absage prüfen und senden',
};

/** Vanilla button colors: info = primary actions, warning, error */
export const DETAIL_ACTION_COLORS: Record<DetailAction, 'info' | 'warning' | 'error'> = {
  send: 'info',
  search: 'info',
  ask: 'info',
  cancel: 'error',
  stop: 'warning',
  choose: 'info',
  reask: 'info',
  sendCancellation: 'info',
};

/** Icons of the actions (modern CTA buttons) */
export const DETAIL_ACTION_ICONS: Record<DetailAction, string> = {
  send: 'mdi-send-outline',
  search: 'mdi-account-search-outline',
  ask: 'mdi-account-plus-outline',
  cancel: 'mdi-cancel',
  stop: 'mdi-timer-off-outline',
  choose: 'mdi-check',
  reask: 'mdi-human-queue',
  sendCancellation: 'mdi-email-check-outline',
};
